import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import apiClient from '../../utils/api';

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1rem;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const SearchSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MembersList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const Thead = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Th = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #dee2e6;
  transition: background 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const NameCell = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const StatusBadge = styled.span<{ active?: boolean; status?: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '활동') return '#d1e7dd';
    if (props.status === '휴면' || props.active) return '#fff3cd';
    return '#d1e7dd';
  }};
  color: ${props => {
    if (props.status === '활동') return '#0f5132';
    if (props.status === '휴면' || props.active) return '#856404';
    return '#0f5132';
  }};
  white-space: nowrap;
  display: inline-block;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #212529;
  border-radius: 8px;
  background: white;
  color: #212529;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #212529;
    color: white;
  }
  
  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean; isNested?: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${props => props.isNested ? 10000 : 9999};
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface DormantMember {
  user_no: number;
  name: string;
  total_count: number;
  active_count: number;
  has_active: boolean;
}

interface DormantHistory {
  dormant_id: number;
  user_no: number;
  name: string;
  start_date: string;
  end_date: string | null;
  dor_reason?: string;  // 휴면 사유
  created_at: string;
  updated_at: string | null;
  is_active: boolean;
}

interface DormantRequest {
  request_id: number;
  user_no: number;
  name: string;
  start_date: string;
  reason: string;
  status: string;
  created_at: string;
  approved_at: string | null;
  rejected_at: string | null;
}

const AdminDormant: React.FC = () => {
  const [dormantList, setDormantList] = useState<DormantMember[]>([]);
  const [dormantRequests, setDormantRequests] = useState<DormantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DormantMember | null>(null);
  const [userHistory, setUserHistory] = useState<DormantHistory[]>([]);
  const [editingDormant, setEditingDormant] = useState<DormantHistory | null>(null);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    dor_reason: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  const fetchDormantHistory = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/dormant-history');
      if (response.data.success) {
        setDormantList(response.data.data);
      }
    } catch (error) {
      console.error('휴면 이력 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDormantRequests = async () => {
    try {
      const response = await apiClient.get('/dormant-requests?status=대기');
      if (response.data.success) {
        setDormantRequests(response.data.data);
      }
    } catch (error) {
      console.error('휴면 신청 목록 조회 실패:', error);
    }
  };

  useEffect(() => {
    fetchDormantHistory();
    fetchDormantRequests();
  }, []);

  const handleApproveRequest = async (requestId: number) => {
    if (!window.confirm('이 휴면 신청을 승인하시겠습니까?')) {
      return;
    }

    try {
      const response = await apiClient.put(`/dormant-requests/${requestId}/approve`);
      if (response.data.success) {
        window.alert('휴면 신청이 승인되었습니다');
        fetchDormantHistory();
        fetchDormantRequests();
      }
    } catch (error: any) {
      console.error('휴면 신청 승인 실패:', error);
      window.alert(error.response?.data?.detail || '휴면 신청 승인에 실패했습니다');
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    if (!window.confirm('이 휴면 신청을 거부하시겠습니까?')) {
      return;
    }

    try {
      const response = await apiClient.put(`/dormant-requests/${requestId}/reject`);
      if (response.data.success) {
        window.alert('휴면 신청이 거부되었습니다');
        fetchDormantRequests();
      }
    } catch (error: any) {
      console.error('휴면 신청 거부 실패:', error);
      window.alert(error.response?.data?.detail || '휴면 신청 거부에 실패했습니다');
    }
  };

  const handleUserClick = async (member: DormantMember) => {
    setSelectedUser(member);
    setIsDetailModalOpen(true);
    
    try {
      const response = await apiClient.get(`/dormant-history/user/${member.user_no}`);
      if (response.data.success) {
        setUserHistory(response.data.data);
      }
    } catch (error) {
      console.error('휴면 이력 조회 실패:', error);
      setUserHistory([]);
    }
  };

  const handleEdit = (dormant: DormantHistory) => {
    setEditingDormant(dormant);
    setFormData({
      start_date: dormant.start_date,
      end_date: dormant.end_date || '',
      dor_reason: dormant.dor_reason || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDormant) return;

    try {
      const params = new URLSearchParams();
      params.append('start_date', formData.start_date);
      if (formData.end_date) {
        params.append('end_date', formData.end_date);
      }
      if (formData.dor_reason) {
        params.append('dor_reason', formData.dor_reason);
      }
      
      const response = await apiClient.put(
        `/dormant-history/${editingDormant.dormant_id}?${params.toString()}`
      );

      if (response.data.success) {
        window.alert('휴면 기간이 수정되었습니다');
        fetchDormantHistory();
        setIsModalOpen(false);
        setEditingDormant(null);
        // 상세 모달이 열려있으면 새로고침
        if (selectedUser) {
          handleUserClick(selectedUser);
        }
      }
    } catch (error: any) {
      console.error('휴면 기간 수정 실패:', error);
      window.alert(error.response?.data?.detail || '휴면 기간 수정에 실패했습니다');
    }
  };

  const handleDelete = async (dormant: DormantHistory) => {
    if (!window.confirm(`정말로 이 휴면 기록을 삭제하시겠습니까?\n\n${dormant.name}님의 휴면 기록이 삭제됩니다.`)) {
      return;
    }

    try {
      const response = await apiClient.delete(`/dormant-history/${dormant.dormant_id}`);

      if (response.data.success) {
        window.alert('휴면 기록이 삭제되었습니다');
        fetchDormantHistory();
        // 상세 모달이 열려있으면 새로고침
        if (selectedUser) {
          handleUserClick(selectedUser);
        }
      }
    } catch (error: any) {
      console.error('휴면 기록 삭제 실패:', error);
      window.alert(error.response?.data?.detail || '휴면 기록 삭제에 실패했습니다');
    }
  };

  // 검색어로 필터링된 목록
  const filteredDormantList = dormantList.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 통계
  const stats = {
    total: dormantList.length,
    active: dormantList.filter(m => m.has_active).length,
    inactive: dormantList.filter(m => !m.has_active).length,
  };

  return (
    <DashboardLayout title="휴면 관리">
      <Container>
        <Header>
          <Title>휴면 관리</Title>
        </Header>

        <StatsRow>
          <StatCard>
            <StatLabel>전체</StatLabel>
            <StatValue>{stats.total}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>휴면 중</StatLabel>
            <StatValue>{stats.active}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>활동 중</StatLabel>
            <StatValue>{stats.inactive}명</StatValue>
          </StatCard>
        </StatsRow>

        {/* 휴면 신청 목록 */}
        {dormantRequests.length > 0 && (
          <SearchSection>
            <SectionTitle>
              휴면 신청 대기 ({dormantRequests.length}건)
            </SectionTitle>
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>이름</Th>
                    <Th>휴면 시작일</Th>
                    <Th>휴면 사유</Th>
                    <Th>신청일</Th>
                    <Th>작업</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {dormantRequests.map((request) => (
                    <Tr key={request.request_id}>
                      <NameCell>{request.name}</NameCell>
                      <Td>{request.start_date}</Td>
                      <Td>{request.reason || '-'}</Td>
                      <Td>{request.created_at.split(' ')[0]}</Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <ActionButton 
                            onClick={() => handleApproveRequest(request.request_id)}
                            style={{ background: '#28a745', color: 'white', borderColor: '#28a745' }}
                          >
                            승인
                          </ActionButton>
                          <ActionButton 
                            onClick={() => handleRejectRequest(request.request_id)}
                            style={{ background: '#dc3545', color: 'white', borderColor: '#dc3545' }}
                          >
                            거부
                          </ActionButton>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </SearchSection>
        )}

        <SearchSection>
          <SearchRow>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="이름으로 검색"
            />
            <Button 
              variant="primary"
              onClick={() => {}}
            >
              검색
            </Button>
          </SearchRow>
        </SearchSection>

        <MembersList>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : filteredDormantList.length > 0 ? (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>이름</Th>
                    <Th>휴면 횟수</Th>
                    <Th>현재 상태</Th>
                    <Th>작업</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredDormantList.map((member) => (
                    <Tr 
                      key={member.user_no}
                      onClick={() => handleUserClick(member)}
                    >
                      <NameCell>{member.name}</NameCell>
                      <Td>{member.total_count}회</Td>
                      <Td>
                        <StatusBadge active={member.has_active} status={member.has_active ? '휴면' : '활동'}>
                          {member.has_active ? '휴면 중' : '활동 중'}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(member);
                        }}>
                          상세보기
                        </ActionButton>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>
              {searchQuery ? '검색 결과가 없습니다.' : '휴면 이력이 없습니다.'}
            </EmptyState>
          )}
        </MembersList>

        {/* 수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          isNested={isDetailModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>휴면 기간 수정</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalForm onSubmit={handleSubmit}>
              <Input
                label="이름"
                type="text"
                value={editingDormant?.name || ''}
                onChange={() => {}}
                disabled
              />
              
              <Input
                label="휴면 시작일"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
              
              <Input
                label="휴면 종료일 (비워두면 현재 휴면 중)"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
              
              <Input
                label="휴면 사유"
                type="text"
                value={formData.dor_reason}
                onChange={(e) => setFormData({ ...formData, dor_reason: e.target.value })}
                placeholder="휴면 사유를 입력하세요"
              />
              
              <ModalActions>
                <Button
                  type="button"
                  variant="outline"
                  size="large"
                  fullWidth
                  onClick={() => setIsModalOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  수정
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
        
        {/* 회원별 휴면 이력 상세 모달 */}
        <ModalOverlay 
          isOpen={isDetailModalOpen}
          onClick={() => {
            setIsDetailModalOpen(false);
            setSelectedUser(null);
            setUserHistory([]);
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <ModalHeader>
              <ModalTitle>
                {selectedUser?.name}님의 휴면 이력 ({selectedUser?.total_count}회)
              </ModalTitle>
              <CloseButton onClick={() => {
                setIsDetailModalOpen(false);
                setSelectedUser(null);
                setUserHistory([]);
              }}>×</CloseButton>
            </ModalHeader>
            
            {userHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>로딩 중...</div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#6c757d' }}>
                  총 {userHistory.length}건의 휴면 기록
                </div>
                
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <Table>
                    <Thead>
                      <tr>
                        <Th>시작일</Th>
                        <Th>종료일</Th>
                        <Th>휴면 사유</Th>
                        <Th>상태</Th>
                        <Th>작업</Th>
                      </tr>
                    </Thead>
                    <Tbody>
                      {userHistory.map((history) => (
                        <Tr key={history.dormant_id}>
                          <Td>{history.start_date}</Td>
                          <Td>{history.end_date || '-'}</Td>
                          <Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {history.dor_reason || '-'}
                          </Td>
                          <Td>
                            <StatusBadge active={history.is_active} status={history.is_active ? '휴면' : '활동'}>
                              {history.is_active ? '휴면 중' : '휴면 종료'}
                            </StatusBadge>
                          </Td>
                          <Td>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <ActionButton 
                                onClick={() => handleEdit(history)}
                              >
                                수정
                              </ActionButton>
                              <ActionButton 
                                onClick={() => handleDelete(history)}
                                style={{ background: '#dc3545', color: 'white', borderColor: '#dc3545' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#c82333';
                                  e.currentTarget.style.borderColor = '#c82333';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#dc3545';
                                  e.currentTarget.style.borderColor = '#dc3545';
                                }}
                              >
                                삭제
                              </ActionButton>
                            </div>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                
                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionButton onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedUser(null);
                    setUserHistory([]);
                  }}>
                    닫기
                  </ActionButton>
                </div>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      </Container>
    </DashboardLayout>
  );
};

export default AdminDormant;

